/**
 * Affiliate / partner signup attribution.
 * ----------------------------------------
 * When a partner shares a link like `https://cevee.eu/signup?ref=hackathonhub`,
 * we capture the code, stash it (first-touch), and attach it to the user's
 * Supabase user_metadata on signup. The backend re-validates it against the
 * same allowlist before persisting it on the profile (see profile-service).
 *
 * The stored code is INTERNAL only — it is never shown to the user and never
 * returned by the profile API. It exists purely so Cevee can, manually,
 * attribute a signup to a partner for commission purposes.
 *
 * Adding a partner = add a slug here (and in the backend allowlist), then hand
 * the partner the link `https://cevee.eu/signup?ref=<slug>`.
 */

/** URL query parameter that carries the partner code. */
export const AFFILIATE_PARAM = 'ref';

/**
 * Known partner slugs. Only codes on this list are captured/stored; anything
 * else in `?ref=` is ignored. Keep slugs stable once handed out — changing a
 * slug breaks links already in the wild.
 */
export const AFFILIATE_ALLOWLIST: readonly string[] = [
  'hackathonhub',
];

/**
 * Normalise a raw `?ref=` value to a comparable slug: lowercase, trimmed, and
 * restricted to URL-safe slug characters. Returns '' if nothing usable remains.
 */
export function normalizeAffiliateCode(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 100);
}

/** True if the (already normalised or raw) code is a known partner slug. */
export function isAllowedAffiliate(raw: string | null | undefined): boolean {
  const code = normalizeAffiliateCode(raw);
  return code.length > 0 && AFFILIATE_ALLOWLIST.includes(code);
}
